import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";

const routes = [
  { path: "/", name: "Home", component: () => import("@/pages/Home.vue") },
  { path: "/auth/login", name: "Login", component: () => import("@/pages/Login.vue") },
  { path: "/auth/register", name: "Register", component: () => import("@/pages/Register.vue") },
  { path: "/cpa", name: "CpaHome", component: () => import("@/pages/Courses.vue") },
  { path: "/cpa/bank", name: "CpaBank", component: () => import("@/pages/CpaBank.vue") },
  { path: "/cpa/bank/:id", name: "CpaBankDetail", component: () => import("@/pages/CpaBankDetail.vue") },
  { path: "/cpa/bank/detail", name: "CpaBankDetailQuery", component: () => import("@/pages/CpaBankDetail.vue") },
  { path: "/cpa/knowledge", name: "CpaKnowledge", component: () => import("@/pages/CpaKnowledge.vue") },
  { path: "/cpa/knowledge-base", name: "CpaKnowledgeBase", component: () => import("@/pages/CpaKnowledgeBase.vue"), meta: { requiresAuth: true } },
  { path: "/cpa/learn/:subject/:chapter", name: "CpaLearnChapter", component: () => import("@/pages/CpaLearnChapter.vue") },
  { path: "/cpa/learn/chapter", name: "CpaLearnChapterQuery", component: () => import("@/pages/CpaLearnChapter.vue") },
  { path: "/cpa/study-plan", name: "CpaStudyPlan", component: () => import("@/pages/CpaStudyPlan.vue"), meta: { requiresAuth: true } },
  { path: "/cpa/mock-exam", name: "CpaMockExam", component: () => import("@/pages/CpaMockExam.vue"), meta: { requiresAuth: true } },
  { path: "/cpa/progress", redirect: "/cpa/practice-records" },
  { path: "/cpa/practice-records", name: "CpaPracticeRecords", component: () => import("@/pages/CpaPracticeRecords.vue"), meta: { requiresAuth: true } },
  { path: "/cpa/learning-method", name: "CpaLearningMethod", component: () => import("@/pages/CpaLearningMethod.vue") },
  { path: "/cpa/onboarding", name: "CpaOnboarding", component: () => import("@/pages/CpaOnboarding.vue") },
  { path: "/cpa/news", name: "CpaNews", component: () => import("@/pages/CpaNews.vue") },
  { path: "/cpa/standards", name: "CpaStandards", component: () => import("@/pages/CpaStandards.vue") },
  { path: "/cpa/audit-standards", name: "CpaAuditStandards", component: () => import("@/pages/CpaAuditStandards.vue") },
  { path: "/tool", name: "Tool", component: () => import("@/pages/Tools.vue") },
  { path: "/tool/guide", name: "ToolGuide", component: () => import("@/pages/ToolGuide.vue") },
  { path: "/project", name: "Project", component: () => import("@/pages/Project.vue"), meta: { requiresAuth: true } },
  { path: "/feedback", name: "Feedback", component: () => import("@/pages/Feedback.vue") },
  { path: "/download", name: "Download", component: () => import("@/pages/Download.vue") },
  { path: "/auth/verify-email", name: "VerifyEmail", component: () => import("@/pages/VerifyEmail.vue") },
  { path: "/auth/verify-required", name: "VerifyRequired", component: () => import("@/pages/VerifyRequired.vue"), meta: { requiresAuth: true } },
  { path: "/account", name: "Account", component: () => import("@/pages/Account.vue"), meta: { requiresAuth: true } },
  { path: "/admin", name: "Admin", component: () => import("@/pages/Admin.vue"), meta: { requiresAdmin: true } },
  { path: "/admin/docs", name: "AdminDocs", component: () => import("@/pages/AdminDocs.vue"), meta: { requiresAdmin: true } },
  { path: "/admin/finance", name: "AdminFinance", component: () => import("@/pages/AdminFinance.vue"), meta: { requiresFinanceAdmin: true } },
  { path: "/finance", name: "Finance", component: () => import("@/pages/FinanceHome.vue"), meta: { requiresAuth: true } },
  { path: "/finance/stocks", redirect: "/finance" },
  { path: "/finance/stock/:tsCode", name: "FinanceStock", component: () => import("@/pages/finance/StockDetail.vue"), meta: { requiresAuth: true } },
  { path: "/finance/berkshire", name: "FinanceBerkshire", component: () => import("@/pages/finance/Berkshire.vue"), meta: { requiresAuth: true } },
  { path: "/finance/news", name: "FinanceNews", component: () => import("@/pages/finance/FinanceNews.vue"), meta: { requiresAuth: true } },
  { path: "/finance/insights", name: "FinanceStockInsights", component: () => import("@/pages/finance/StockInsights.vue"), meta: { requiresAuth: true } },
  { path: "/finance/favorites", redirect: "/finance" },
  { path: "/mindchat", name: "MindChat", component: () => import("@/pages/MindChat.vue"), meta: { requiresAuth: true } },
  { path: "/audit-assistant", name: "AuditAssistant", component: () => import("@/pages/AuditAssistant.vue"), meta: { requiresAuth: true } },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, _from, next) => {
  const auth = useAuthStore();
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    next("/auth/login");
    return;
  }
  if (to.meta.requiresAdmin && !auth.isAdmin) {
    next("/");
    return;
  }
  if (to.meta.requiresFinanceAdmin && !auth.isFinanceAdmin) {
    next("/");
    return;
  }
  // 强制邮箱验证：已登录、非管理员、邮箱未验证时，访问需登录页面一律引导到验证引导页
  // （后端 securityGuard 是最终防线，这里做前端体验层拦截）
  if (
    to.meta.requiresAuth &&
    auth.isAuthenticated &&
    !auth.isAdmin &&
    auth.user?.email_verified === false &&
    to.path !== "/auth/verify-required"
  ) {
    next("/auth/verify-required");
    return;
  }
  next();
});

export default router;
